import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  Box,
  Modal,
  Typography,
  List,
  ListItem,
  ListItemButton,
  Button,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import StoreOutlinedIcon from '@mui/icons-material/StoreOutlined';

import Loader from '../common/loader/loader.component';

import styles from './settings-modal.module.scss';
export interface SettingsOption {
  text: string;
  actionId: string;
  withIcon?: boolean;
}

interface SettingsModalProps {
  isOpen: boolean;
  containerId: () => HTMLElement | null;
  options: SettingsOption[];
  handleClose: () => void;
  handleAction: (actionId: string) => void;
  title: string;
  subtitle?: string;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  containerId,
  options,
  handleClose,
  handleAction,
  title,
  subtitle,
}) => {
  const { t } = useTranslation();
  const fetchingUserIdentify = useSelector(
    (state: any) => state.winkLogin.fetchingUserIdentify
  );

  const merchantSelected = useSelector(
    (state: any) => state.superAdmin.merchantSelected
  );
  const rowColors = useSelector(
    (state: any) => state.superAdmin.tableRowColors.activeMerchants
  );

  const merchantId = merchantSelected?.merchantId;
  const gradient = merchantId
    ? rowColors[merchantId]
    : 'linear-gradient(132deg, #CCC, #EEE)';

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby='settings-modal-title'
      aria-describedby='settings-modal-description'
      id='settings_modal'
      container={containerId}
      sx={{
        position: 'absolute',
      }}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: 'rgb(0 0 0 / 18%)',
          },
        },
      }}
    >
      <Box className={styles.modalBox}>
        <Box
          sx={{
            maxWidth: '80%',
          }}
        >
          <Box
            sx={{
              background: `${gradient}`,
              display: 'flex',
              justifyContent: 'center',
              height: '45px',
              width: '45px',
              padding: '20px',
              margin: 'auto',
              my: '12px',
              borderRadius: '6px',
              alignItems: 'center',
              '@media (max-width: 600px)': {
                height: '30px',
                width: '30px',
                marginRight: '10px',
              },
            }}
          >
            <StoreOutlinedIcon
              sx={{
                marginTop: '4px',
                color: 'white',
                marginLeft: '4px',
                marginRight: '4px',
                width: '5rem',
                height: 'auto',
                '@media (max-width: 600px)': {
                  width: '20px',
                  height: '20px',
                },
              }}
            />
          </Box>

          <Typography variant='h6' component='h2' className={styles.modalTitle}>
            {title}
            {subtitle ? (
              <span className={styles.modalSubTitle}>{` ${subtitle}`}</span>
            ) : (
              ''
            )}
          </Typography>
          {fetchingUserIdentify ? (
            <Loader loading />
          ) : (
            <List className={styles.list}>
              {options.map((option, index) => (
                <ListItem
                  key={index}
                  onClick={() => handleAction(option.actionId)}
                  className={styles.listItem}
                >
                  <ListItemButton className={styles.listItemButton}>
                    <ListItemText
                      className={styles.listItemText}
                      primary={option.text}
                      primaryTypographyProps={{
                        fontWeight: 500,
                        fontSize: '15px',
                      }}
                    />
                    {option.withIcon && (
                      <ListItemIcon className={styles.listItemIcon}>
                        <SettingsIcon className={styles.icon} />
                      </ListItemIcon>
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </Box>

        <Button
          onClick={handleClose}
          variant='contained'
          className={styles.closeButton}
        >
          {t('LABEL.CLOSE')}
        </Button>
      </Box>
    </Modal>
  );
};

export default SettingsModal;
